import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAuth, useClient } from "../context/auth-context";
import { useTenantContext } from "../context/tenant-context";
import { Tenant, TenantBrandConfig } from "../domain/company";
import { delay } from "../utils/helpers";

export function useTenantDetails() {
  const { isAuthenticated, isLoading: isAuthenticating } = useAuth();
  const client = useClient<Tenant>();
  const { activeTenant } = useTenantContext();

  const { data, isLoading } = useQuery<Tenant, Error>({
    enabled: isAuthenticated,
    queryKey: ["tenant-details", activeTenant],
    queryFn: () => client(`tenants/${activeTenant}`)
  });

  return { data, isLoading: isLoading || isAuthenticating };
}

export function useGetTenantBranding() {
  const tenantDetails = useTenantDetails();
  const { identity } = useAuth();
  const { activeTenant } = useTenantContext();

  const organisationId = identity?.tenantInfo?.[activeTenant]?.organisationId;
  const organisationBrandConfig: TenantBrandConfig | undefined = organisationId
    ? tenantDetails?.data?.organisationBrandConfig?.[organisationId]
    : undefined;
  const brandConfig = organisationBrandConfig ?? tenantDetails?.data?.brandConfig;

  const headerBackgroundColour = brandConfig?.headerBackgroundColour ?? "#2A2532";
  const headerForegroundColour = brandConfig?.headerForegroundColour ?? "#FFFFFF";
  const logoUrl = brandConfig?.logoUrl;
  const inviteEmailBannerUrl = brandConfig?.inviteEmailBannerUrl;
  const inviteEmailProductName = brandConfig?.inviteEmailProductName;

  return {
    headerBackgroundColour,
    headerForegroundColour,
    logoUrl,
    inviteEmailBannerUrl,
    inviteEmailProductName,
    isLoading: tenantDetails.isLoading
  };
}

export function useGetTenantBrandingForOrganisation(organisationId?: string) {
  const tenantDetails = useTenantDetails();

  const organisationBrandConfig = organisationId
    ? tenantDetails?.data?.organisationBrandConfig?.[organisationId]
    : undefined;

  const headerBackgroundColour =
    organisationBrandConfig?.headerBackgroundColour ??
    tenantDetails?.data?.brandConfig?.headerBackgroundColour ??
    "#2A2532";
  const headerForegroundColour =
    organisationBrandConfig?.headerForegroundColour ??
    tenantDetails?.data?.brandConfig?.headerForegroundColour ??
    "#FFFFFF";
  const logoUrl = organisationBrandConfig?.logoUrl ?? tenantDetails?.data?.brandConfig?.logoUrl;
  const inviteEmailBannerUrl =
    organisationBrandConfig?.inviteEmailBannerUrl ?? tenantDetails?.data?.brandConfig?.inviteEmailBannerUrl;
  const inviteEmailProductName =
    organisationBrandConfig?.inviteEmailProductName ?? tenantDetails?.data?.brandConfig?.inviteEmailProductName;

  return {
    headerBackgroundColour,
    headerForegroundColour,
    logoUrl,
    inviteEmailBannerUrl,
    inviteEmailProductName,
    isLoading: tenantDetails.isLoading
  };
}

export enum BrandingFileType {
  Logo = "logo",
  InviteEmailBanner = "inviteEmailBanner"
}

interface ApplyTenantBrandingCommand {
  headerBackgroundColour?: string;
  headerForegroundColour?: string;
  logoFile?: File | null;
  inviteEmailBannerFile?: File | null;
  inviteEmailProductName?: string;
}

export function useApplyTenantBranding(organisationId?: string) {
  const client = useClient();
  const { activeTenant } = useTenantContext();
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation<unknown, Error, ApplyTenantBrandingCommand>(
    command => {
      const formData = new FormData();
      const fileMapping: BrandingFileType[] = [];
      if (!!command.logoFile) {
        fileMapping.push(BrandingFileType.Logo);
      }
      if (!!command.inviteEmailBannerFile) {
        fileMapping.push(BrandingFileType.InviteEmailBanner);
      }

      const data: any = {
        headerBackgroundColour: command.headerBackgroundColour,
        headerForegroundColour: command.headerForegroundColour,
        inviteEmailProductName: command.inviteEmailProductName
      };
      if (fileMapping.length > 0) {
        data.fileMappings = fileMapping;
      }

      formData.append("data", JSON.stringify(data));

      if (!!command.logoFile) {
        formData.append("files", command.logoFile);
      }
      if (!!command.inviteEmailBannerFile) {
        formData.append("files", command.inviteEmailBannerFile);
      }

      const url = organisationId
        ? `tenants/${activeTenant}/branding/organisations/${organisationId}`
        : `tenants/${activeTenant}/branding`;

      return client(url, {
        method: "PUT",
        data: formData,
        formData: true
      });
    },
    {
      onSuccess: _ => delay(1000).then(() => queryClient.invalidateQueries(["tenant-details", activeTenant]))
    }
  );

  return { mutate, isLoading };
}

export function useResetTenantBranding(organisationId?: string) {
  const client = useClient();
  const { activeTenant } = useTenantContext();
  const queryClient = useQueryClient();

  const { mutate } = useMutation(
    () => {
      const url = organisationId
        ? `tenants/${activeTenant}/branding/organisations/${organisationId}`
        : `tenants/${activeTenant}/branding`;

      return client(url, {
        method: "DELETE"
      });
    },
    {
      onSuccess: _ => delay(1000).then(() => queryClient.invalidateQueries(["tenant-details", activeTenant]))
    }
  );

  return { mutate, isLoading: false };
}
